import { useState, useRef, useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'

type SiteTheme = 'classic' | 'aero' | 'aero-claude'
type DashboardVersion = 'v1' | 'v2'

export default function Layout() {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, logout } = useAuthStore()
    const [userMenuOpen, setUserMenuOpen] = useState(false)
    const [siteTheme, setSiteTheme] = useState<SiteTheme>(() => {
        const saved = typeof window !== 'undefined' ? localStorage.getItem('site_theme_preference') : null
        if (saved === 'aero' || saved === 'aero-claude') return saved
        return 'classic'
    })
    const userMenuRef = useRef<HTMLDivElement>(null)

    const [dashboardVersion, setDashboardVersion] = useState<DashboardVersion>(() => {
        if (typeof window === 'undefined') return 'v1'
        const fromPath = window.location.pathname.startsWith('/v2/dashboard') ? 'v2' : 'v1'
        const saved = localStorage.getItem('dashboard_version_preference') as DashboardVersion | null
        return saved === 'v2' ? 'v2' : fromPath
    })
    const [aiChatPanelOpen, setAiChatPanelOpen] = useState(false)

    const isActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/')
    }

    const isDashboardRoute =
        location.pathname === '/' ||
        location.pathname === '/dashboard' ||
        location.pathname.startsWith('/dashboard/') ||
        location.pathname === '/v2/dashboard'

    const isAiChatCapableRoute =
        location.pathname.startsWith('/screener') ||
        location.pathname.startsWith('/v2/screener') ||
        location.pathname.startsWith('/tables') ||
        location.pathname.startsWith('/v2/tables') ||
        location.pathname.startsWith('/watch-lists') ||
        location.pathname.startsWith('/v2/watch-lists') ||
        location.pathname.startsWith('/portfolios') ||
        location.pathname.startsWith('/v2/portfolios') ||
        location.pathname.startsWith('/v2/stocks/')
    const uiFrontendVersion: 'v1' | 'v2' = location.pathname.startsWith('/v2/') ? 'v2' : 'v1'

    const getLinkClass = (path: string) => {
        const baseClass = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
        const activeClass = siteTheme === 'aero-claude'
            ? "nav-active-aero font-semibold shadow-sm"
            : "text-amber-300 bg-zinc-950 font-semibold ring-1 ring-zinc-700"
        const inactiveClass = siteTheme === 'aero-claude'
            ? "text-zinc-600 hover:text-zinc-900 hover:bg-[#EDE9E3]"
            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-700"
        return `${baseClass} ${isActive(path) ? activeClass : inactiveClass}`
    }

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    const handleToggleAiChat = () => {
        if (isAiChatCapableRoute) {
            window.dispatchEvent(new CustomEvent('folio:toggle-ai-chat'))
        } else {
            navigate('/screener?openChat=1')
        }
    }

    useEffect(() => {
        const onPanel = (e: Event) => {
            const ce = e as CustomEvent<{ open?: boolean }>
            if (typeof ce.detail?.open === 'boolean') {
                setAiChatPanelOpen(ce.detail.open)
            }
        }
        window.addEventListener('folio:ai-chat-panel', onPanel)
        return () => window.removeEventListener('folio:ai-chat-panel', onPanel)
    }, [])

    useEffect(() => {
        if (!isAiChatCapableRoute) {
            setAiChatPanelOpen(false)
        }
    }, [location.pathname, isAiChatCapableRoute])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setUserMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return
        localStorage.setItem('dashboard_version_preference', dashboardVersion)
    }, [dashboardVersion])

    useEffect(() => {
        // Keep URL and version preference in sync when user navigates directly
        if (location.pathname.startsWith('/v2/dashboard') && dashboardVersion !== 'v2') {
            setDashboardVersion('v2')
        } else if (!location.pathname.startsWith('/v2/dashboard') && dashboardVersion !== 'v1') {
            // Only treat root and classic dashboard as v1; other pages shouldn't flip preference
            if (location.pathname === '/' || location.pathname.startsWith('/dashboard')) {
                setDashboardVersion('v1')
            }
        }
    }, [location.pathname, dashboardVersion])

    const handleFrontendVersionChange = (version: 'v1' | 'v2') => {
        setDashboardVersion(version)
        setUserMenuOpen(false)

        const toV2Path = () => {
            const p = location.pathname
            if (p === '/' || p === '/dashboard' || p.startsWith('/dashboard/')) return '/v2/dashboard'
            if (p === '/screener' || p.startsWith('/screener/')) return '/v2/screener'
            if (p === '/tables' || p.startsWith('/tables/')) return '/v2/tables'
            if (p === '/watch-lists' || p.startsWith('/watch-lists/')) return '/v2/watch-lists'
            if (p === '/portfolios' || p.startsWith('/portfolios/')) return '/v2/portfolios'
            const stockMatch = p.match(/^\/stocks\/([^/]+)$/)
            if (stockMatch) return `/v2/stocks/${stockMatch[1]}`
            return '/v2/dashboard'
        }

        const toV1Path = () => {
            const p = location.pathname
            if (p === '/v2/dashboard') return '/dashboard'
            if (p === '/v2/screener' || p.startsWith('/v2/screener/')) return '/screener'
            if (p === '/v2/tables' || p.startsWith('/v2/tables/')) return '/tables'
            if (p === '/v2/watch-lists' || p.startsWith('/v2/watch-lists/')) return '/watch-lists'
            if (p === '/v2/portfolios' || p.startsWith('/v2/portfolios/')) return '/portfolios'
            const stockMatch = p.match(/^\/v2\/stocks\/([^/]+)$/)
            if (stockMatch) return `/stocks/${stockMatch[1]}`
            return '/dashboard'
        }

        navigate(version === 'v2' ? toV2Path() : toV1Path())
    }

    const handleThemePick = (value: SiteTheme) => {
        setSiteTheme(value)
        setUserMenuOpen(false)
    }

    useEffect(() => {
        localStorage.setItem('site_theme_preference', siteTheme)
        document.documentElement.classList.toggle('theme-aero', siteTheme === 'aero')
        document.body.classList.toggle('theme-aero', siteTheme === 'aero')
        document.documentElement.classList.toggle('theme-aero-claude', siteTheme === 'aero-claude')
        document.body.classList.toggle('theme-aero-claude', siteTheme === 'aero-claude')
    }, [siteTheme])

    return (
        <div className="min-h-screen flex flex-col bg-zinc-950">
            {/* Navigation */}
            <nav className="sticky top-0 z-[260] bg-zinc-900 border-b border-zinc-800 shadow-lg">
                <div className="px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <Link to="/dashboard" className="flex items-center gap-2 group">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">AI</span>
                            </div>
                            <h1 className="text-xl font-bold text-gradient hidden sm:inline">Aero</h1>
                        </Link>

                        {/* Menu */}
                        <div className="flex items-center gap-6">
                            <div className="hidden sm:flex items-center gap-1">
                                <Link
                                    to="/dashboard"
                                    className={getLinkClass("/dashboard")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l9-9 9 9M5 10v10h14V10" />
                                    </svg>
                                    Dashboard
                                </Link>
                                <Link
                                    to="/screener"
                                    className={getLinkClass("/screener")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M6 12h12M9 18h6" />
                                    </svg>
                                    Screener
                                </Link>
                                <Link
                                    to="/tables"
                                    className={getLinkClass("/tables")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <rect x="3" y="4" width="18" height="16" rx="2" strokeWidth="2" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M9 4v16M15 4v16" />
                                    </svg>
                                    Tables
                                </Link>
                                <Link
                                    to="/watch-lists"
                                    className={getLinkClass("/watch-lists")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 4h14v16l-7-4-7 4V4z" />
                                    </svg>
                                    Watch Lists
                                </Link>
                                <Link
                                    to="/portfolios"
                                    className={getLinkClass("/portfolios")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18v13H3zM3 10h18M8 7V5h8v2" />
                                    </svg>
                                    Portfolios
                                </Link>
                                <Link
                                    to="/news"
                                    className={getLinkClass("/news")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h14v14H5zM8 9h8M8 13h8M8 17h5" />
                                    </svg>
                                    News
                                </Link>
                                <Link
                                    to="/influencers"
                                    className={getLinkClass("/influencers")}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <circle cx="8" cy="8" r="3" strokeWidth="2" />
                                        <circle cx="16" cy="9" r="2.5" strokeWidth="2" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.5 19a4.5 4.5 0 019 0M12 19a4 4 0 018 0" />
                                    </svg>
                                    Influencers
                                </Link>
                                <button
                                    type="button"
                                    onClick={handleToggleAiChat}
                                    aria-pressed={isAiChatCapableRoute ? aiChatPanelOpen : false}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                        isAiChatCapableRoute && aiChatPanelOpen
                                            ? siteTheme === 'aero-claude'
                                                ? 'nav-active-aero font-semibold shadow-sm'
                                                : 'text-amber-300 bg-zinc-950 font-semibold ring-1 ring-zinc-700'
                                            : siteTheme === 'aero-claude'
                                              ? 'text-zinc-600 hover:text-zinc-900 hover:bg-[#EDE9E3]'
                                              : 'text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800'
                                    }`}
                                >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5M4 6h16v10H8l-4 4V6z" />
                                    </svg>
                                    AI Chat
                                </button>
                            </div>

                            {/* User menu: pill trigger opens capsule panel (account, dashboard, theme) + logout */}
                            {user && (
                                <div className={`flex items-center gap-3 pl-6 border-l ${siteTheme === 'aero-claude' ? 'border-zinc-200' : 'border-zinc-700'}`}>
                                    <div ref={userMenuRef} className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                                            className={
                                                siteTheme === 'aero-claude'
                                                    ? 'flex max-w-[200px] sm:max-w-[260px] items-center gap-2 rounded-full border border-zinc-300/90 bg-white/95 px-4 py-2 text-left text-sm font-medium text-zinc-800 shadow-sm transition-all hover:border-zinc-400 hover:shadow-md'
                                                    : 'flex max-w-[200px] sm:max-w-[260px] items-center gap-2 rounded-full border border-zinc-600 bg-zinc-800/95 px-4 py-2 text-left text-sm font-medium text-zinc-200 shadow-sm transition-all hover:border-zinc-500 hover:bg-zinc-800'
                                            }
                                            aria-expanded={userMenuOpen}
                                            aria-haspopup="menu"
                                        >
                                            <span className="truncate">{user.email}</span>
                                            <svg className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                            </svg>
                                        </button>
                                        {userMenuOpen && (
                                            <div
                                                className={
                                                    siteTheme === 'aero-claude'
                                                        ? 'absolute right-0 top-[calc(100%+8px)] z-[280] min-w-[270px] rounded-[1.25rem] border border-zinc-200 bg-white p-3 shadow-xl'
                                                        : 'absolute right-0 top-[calc(100%+8px)] z-[280] min-w-[270px] rounded-[1.25rem] border border-zinc-600 bg-zinc-900 p-3 shadow-xl'
                                                }
                                                role="menu"
                                            >
                                                <button
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={() => {
                                                        navigate('/account')
                                                        setUserMenuOpen(false)
                                                    }}
                                                    className={
                                                        siteTheme === 'aero-claude'
                                                            ? 'w-full cursor-pointer rounded-full border border-transparent bg-[#F5F3EF] px-3 py-2 text-center text-xs font-semibold text-zinc-900 transition-colors hover:bg-[#EDE9E3]'
                                                            : 'w-full cursor-pointer rounded-full border border-zinc-600 bg-zinc-800 px-3 py-2 text-center text-xs font-semibold text-white transition-colors hover:bg-zinc-700'
                                                    }
                                                >
                                                    Account
                                                </button>

                                                {user?.role === 'admin' && (
                                                    <>
                                                        <div className={`my-3 h-px ${siteTheme === 'aero-claude' ? 'bg-zinc-200' : 'bg-zinc-700'}`} aria-hidden />
                                                        <p
                                                            className={`mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${siteTheme === 'aero-claude' ? 'text-zinc-500' : 'text-zinc-500'}`}
                                                        >
                                                            Admin
                                                        </p>
                                                        <button
                                                            type="button"
                                                            role="menuitem"
                                                            onClick={() => {
                                                                navigate('/user-management')
                                                                setUserMenuOpen(false)
                                                            }}
                                                            className={
                                                                siteTheme === 'aero-claude'
                                                                    ? 'w-full cursor-pointer rounded-xl px-3 py-2 text-left text-xs font-medium text-zinc-900 transition-colors hover:bg-[#F5F3EF]'
                                                                    : 'w-full cursor-pointer rounded-xl px-3 py-2 text-left text-xs font-medium text-white transition-colors hover:bg-zinc-800'
                                                            }
                                                        >
                                                            User Management
                                                        </button>
                                                        <button
                                                            type="button"
                                                            role="menuitem"
                                                            onClick={() => {
                                                                navigate('/influencer-management')
                                                                setUserMenuOpen(false)
                                                            }}
                                                            className={
                                                                siteTheme === 'aero-claude'
                                                                    ? 'w-full cursor-pointer rounded-xl px-3 py-2 text-left text-xs font-medium text-zinc-900 transition-colors hover:bg-[#F5F3EF]'
                                                                    : 'w-full cursor-pointer rounded-xl px-3 py-2 text-left text-xs font-medium text-white transition-colors hover:bg-zinc-800'
                                                            }
                                                        >
                                                            Influencers
                                                        </button>
                                                    </>
                                                )}

                                                <div className={`my-3 h-px ${siteTheme === 'aero-claude' ? 'bg-zinc-200' : 'bg-zinc-700'}`} aria-hidden />

                                                <p className={`mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${siteTheme === 'aero-claude' ? 'text-zinc-500' : 'text-zinc-500'}`}>
                                                    UI Frontend
                                                </p>
                                                <div
                                                    className={
                                                        siteTheme === 'aero-claude'
                                                            ? 'flex gap-1 rounded-full bg-[#EDE9E3] p-0.5'
                                                            : 'flex gap-1 rounded-full bg-zinc-950 p-0.5 ring-1 ring-zinc-700'
                                                    }
                                                >
                                                    {(['v1', 'v2'] as const).map((v) => (
                                                        <button
                                                            key={v}
                                                            type="button"
                                                            onClick={() => handleFrontendVersionChange(v)}
                                                            className={
                                                                uiFrontendVersion === v
                                                                    ? siteTheme === 'aero-claude'
                                                                        ? 'flex-1 rounded-full bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-900 shadow-sm'
                                                                        : 'flex-1 rounded-full bg-zinc-700 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm'
                                                                    : siteTheme === 'aero-claude'
                                                                        ? 'flex-1 rounded-full px-2.5 py-1.5 text-[11px] font-medium text-zinc-600 transition-colors hover:text-zinc-900'
                                                                        : 'flex-1 rounded-full px-2.5 py-1.5 text-[11px] font-medium text-zinc-400 transition-colors hover:text-white'
                                                            }
                                                        >
                                                            {v === 'v1' ? 'v1' : 'v2'}
                                                        </button>
                                                    ))}
                                                </div>

                                                <p className={`mb-1.5 mt-4 text-[10px] font-semibold uppercase tracking-[0.12em] ${siteTheme === 'aero-claude' ? 'text-zinc-500' : 'text-zinc-500'}`}>
                                                    Theme
                                                </p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {([
                                                        ['classic', 'Classic'],
                                                        ['aero', 'Aero'],
                                                        ['aero-claude', 'Aero Claude'],
                                                    ] as Array<[SiteTheme, string]>).map(([value, label]) => (
                                                        <button
                                                            key={value}
                                                            type="button"
                                                            onClick={() => handleThemePick(value)}
                                                            className={
                                                                siteTheme === value
                                                                    ? siteTheme === 'aero-claude'
                                                                        ? 'rounded-full border-2 border-zinc-900 bg-zinc-900 px-2.5 py-1 text-[11px] font-semibold text-white'
                                                                        : 'rounded-full border-2 border-amber-500/80 bg-amber-500/20 px-2.5 py-1 text-[11px] font-semibold text-amber-200'
                                                                    : siteTheme === 'aero-claude'
                                                                        ? 'rounded-full border border-zinc-300 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-700 transition-colors hover:border-zinc-400 hover:bg-[#F5F3EF]'
                                                                        : 'rounded-full border border-zinc-600 bg-zinc-800 px-2.5 py-1 text-[11px] font-medium text-zinc-300 transition-colors hover:border-zinc-500 hover:bg-zinc-700'
                                                            }
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button type="button" onClick={handleLogout} className="btn btn-danger btn-sm">
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className={isDashboardRoute ? 'flex-1 w-full p-0 overflow-hidden' : 'flex-1 w-full pt-4 pb-8 px-4 sm:px-6 lg:px-8'}>
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="bg-zinc-900 border-t border-zinc-800 mt-12">
                <div className="py-8 px-4 sm:px-6 lg:px-8">
                    <p className="text-zinc-400 text-sm text-center">
                        © 2026 FolioView - Multi-Tenant Stock Screener
                    </p>
                </div>
            </footer>
        </div>
    )
}
